const KEY_LAST_COMPANY_CODE = 'lastCompanyCode';
const KEY_LAST_COMPANY_NAME = 'lastCompanyName';
const KEY_LAST_EMAIL = 'lastEmail';
const KEY_REMEMBER_EMAIL = 'rememberEmail';
const KEY_LAST_LOGIN_DATE = 'lastLoginDate';
const KEY_LAST_TENANT_ID = 'lastTenantId';
const KEY_LAST_TENANT_API_URL = 'lastTenantApiUrl';

export interface LoginPreferences {
  lastCompanyCode: string | null;
  lastCompanyName: string | null;
  lastEmail: string | null;
  rememberEmail: boolean;
  lastLoginDate: number | null;
}

/** Last resolved tenant, kept after sign-out so login can skip tenant discovery. */
export interface CachedTenant {
  tenantId: number;
  tenantCode: string;
  tenantName: string;
  apiUrl: string;
}

export const loadLoginPreferences = (): LoginPreferences => {
  try {
    if (typeof window === 'undefined') {
      return { lastCompanyCode: null, lastCompanyName: null, lastEmail: null, rememberEmail: false, lastLoginDate: null };
    }
    const code = localStorage.getItem(KEY_LAST_COMPANY_CODE);
    const name = localStorage.getItem(KEY_LAST_COMPANY_NAME);
    const email = localStorage.getItem(KEY_LAST_EMAIL);
    const remember = localStorage.getItem(KEY_REMEMBER_EMAIL);
    const lastLogin = localStorage.getItem(KEY_LAST_LOGIN_DATE);
    return {
      lastCompanyCode: code,
      lastCompanyName: name,
      lastEmail: remember === 'true' ? email : null,
      rememberEmail: remember === 'true',
      lastLoginDate: lastLogin ? parseInt(lastLogin, 10) : null,
    };
  } catch {
    return {
      lastCompanyCode: null,
      lastCompanyName: null,
      lastEmail: null,
      rememberEmail: false,
      lastLoginDate: null,
    };
  }
};

export const loadCachedTenant = (): CachedTenant | null => {
  try {
    if (typeof window === 'undefined') return null;
    const idRaw = localStorage.getItem(KEY_LAST_TENANT_ID);
    const apiUrl = localStorage.getItem(KEY_LAST_TENANT_API_URL)?.trim();
    const code = localStorage.getItem(KEY_LAST_COMPANY_CODE)?.trim();
    const name = localStorage.getItem(KEY_LAST_COMPANY_NAME)?.trim();
    const tenantId = idRaw ? parseInt(idRaw, 10) : NaN;
    if (!Number.isFinite(tenantId) || tenantId <= 0 || !apiUrl || !code) return null;
    return {
      tenantId,
      tenantCode: code,
      tenantName: name || code,
      apiUrl,
    };
  } catch {
    return null;
  }
};

export const saveCachedTenant = (tenant: {
  tenantId: number;
  tenantCode: string;
  tenantName?: string | null;
  apiUrl: string;
}): void => {
  try {
    if (typeof window === 'undefined') return;
    const apiUrl = tenant.apiUrl?.trim();
    if (!tenant.tenantId || !tenant.tenantCode?.trim() || !apiUrl) return;
    localStorage.setItem(KEY_LAST_TENANT_ID, String(tenant.tenantId));
    localStorage.setItem(KEY_LAST_TENANT_API_URL, apiUrl);
    localStorage.setItem(KEY_LAST_COMPANY_CODE, tenant.tenantCode.trim());
    if (tenant.tenantName?.trim()) {
      localStorage.setItem(KEY_LAST_COMPANY_NAME, tenant.tenantName.trim());
    }
  } catch (e) {
    console.warn('[LoginPreferences] Failed to save cached tenant:', e);
  }
};

/** Copy session tenant fields into the login cache before logout clears them. */
export const snapshotCachedTenantFromSession = (): void => {
  try {
    if (typeof window === 'undefined') return;
    const idRaw = localStorage.getItem('tenantId');
    const apiUrl = localStorage.getItem('tenantApiUrl')?.trim();
    const code = localStorage.getItem(KEY_LAST_COMPANY_CODE)?.trim();
    const name = localStorage.getItem(KEY_LAST_COMPANY_NAME)?.trim();
    const tenantId = idRaw ? parseInt(idRaw, 10) : NaN;
    if (!Number.isFinite(tenantId) || tenantId <= 0 || !apiUrl || !code) return;
    saveCachedTenant({ tenantId, tenantCode: code, tenantName: name, apiUrl });
  } catch {
    /* ignore */
  }
};

export function cachedTenantToResolveResponse(tenant: CachedTenant): {
  tenantId: number;
  tenantName: string;
  tenantCode: string;
  isActive: boolean;
  apiUrl: string;
} {
  return {
    tenantId: tenant.tenantId,
    tenantName: tenant.tenantName,
    tenantCode: tenant.tenantCode,
    isActive: true,
    apiUrl: tenant.apiUrl,
  };
}

export function readCachedTenantForIdentifier(identifier: string): ReturnType<typeof cachedTenantToResolveResponse> | null {
  const cached = loadCachedTenant();
  if (!cached || !identifier.trim() || !tenantMatchesIdentifier(cached, identifier)) return null;
  return cachedTenantToResolveResponse(cached);
}

export const tenantMatchesIdentifier = (
  tenant: { tenantCode: string; tenantName: string },
  identifier: string
): boolean => {
  const id = identifier.trim().toLowerCase();
  if (!id) return false;
  return tenant.tenantCode.trim().toLowerCase() === id
    || tenant.tenantName.trim().toLowerCase() === id;
};

export const saveLoginPreferences = (prefs: {
  companyCode: string;
  companyName: string;
  email?: string;
  rememberEmail?: boolean;
  lastLoginDate?: number;
}): void => {
  try {
    if (typeof window === 'undefined') return;
    localStorage.setItem(KEY_LAST_COMPANY_CODE, prefs.companyCode);
    localStorage.setItem(KEY_LAST_COMPANY_NAME, prefs.companyName);
    if (prefs.rememberEmail && prefs.email) {
      localStorage.setItem(KEY_LAST_EMAIL, prefs.email);
      localStorage.setItem(KEY_REMEMBER_EMAIL, 'true');
    } else {
      localStorage.removeItem(KEY_LAST_EMAIL);
      localStorage.setItem(KEY_REMEMBER_EMAIL, 'false');
    }
    if (prefs.lastLoginDate != null) {
      localStorage.setItem(KEY_LAST_LOGIN_DATE, String(prefs.lastLoginDate));
    }
  } catch (e) {
    console.warn('[LoginPreferences] Failed to save:', e);
  }
};
